import { useMemo, type JSX } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { ZodForm } from '../../components/forms/ZodForm.js'
import { FormText } from '../../components/forms/FormText.js'
import { FormSaveButton } from '../../components/forms/FormSaveButton.js'
import { Button } from 'primereact/button'
import {
  countTemplateVariables,
  renderTemplate,
} from '../../utils/templateVars.js'
import type { WhatsAppTemplate } from './types.js'

export interface TemplateVariableFormProps {
  template: WhatsAppTemplate
  onSubmit: (variables: string[]) => void | Promise<void>
  onCancel: () => void
}

function varKey(i: number): string {
  return `v${i + 1}`
}

/** Live preview — reads RHF watch() so it updates as the agent types. */
function Preview(props: {
  bodyText: string
  count: number
}): JSX.Element {
  const { watch } = useFormContext()
  const values = watch()
  const vars = Array.from({ length: props.count }, (_, i) =>
    typeof values[varKey(i)] === 'string' ? (values[varKey(i)] as string) : '',
  )
  return (
    <div className="mt-2 rounded bg-gray-50 px-3 py-2 text-sm">
      <span className="mb-1 block text-xs font-medium text-gray-500">
        Preview
      </span>
      <span data-testid="template-preview">
        {renderTemplate(props.bodyText, vars)}
      </span>
    </div>
  )
}

/**
 * Task 8 (§0.D): the agent fills a template's {{1}}..{{n}} variables with a
 * live preview. Uses the shared count/render helpers (web copy, §0.E source
 * of truth) and the §0.I ZodForm + Form* framework (no raw useState fields).
 * On submit emits the ordered `variables: string[]` (index 1..n).
 */
export default function TemplateVariableForm(
  props: TemplateVariableFormProps,
): JSX.Element {
  const count = countTemplateVariables(props.template.bodyText)

  const schema = useMemo(() => {
    const shape: Record<string, z.ZodString> = {}
    for (let i = 0; i < count; i++) {
      shape[varKey(i)] = z.string().min(1, 'Required')
    }
    return z.object(shape)
  }, [count])

  const defaultValues = useMemo(() => {
    const dv: Record<string, string> = {}
    for (let i = 0; i < count; i++) dv[varKey(i)] = ''
    return dv
  }, [count])

  return (
    <ZodForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={async (data: Record<string, string>) => {
        const ordered = Array.from({ length: count }, (_, i) => data[varKey(i)] ?? '')
        await props.onSubmit(ordered)
      }}
    >
      <div className="space-y-1">
        {count === 0 && (
          <p className="text-sm text-gray-600">
            This template has no variables.
          </p>
        )}
        {Array.from({ length: count }, (_, i) => (
          <FormText
            key={varKey(i)}
            name={varKey(i)}
            label={`Variable {{${i + 1}}}`}
            required
          />
        ))}
        <Preview bodyText={props.template.bodyText} count={count} />
        <div className="mt-3 flex justify-end gap-2">
          <Button
            label="Cancel"
            type="button"
            onClick={props.onCancel}
          />
          <FormSaveButton label="Send" requireDirty={count > 0} />
        </div>
      </div>
    </ZodForm>
  )
}
